import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { message } from 'antd';
import { workwiseServices } from '../../Workw-Api/api';
import { templateService, adminService } from '../../utils/api';
import Sidebar from './components/Sidebar/Sidebar';
import Header from './components/Header/Header';
import CreateGridDrawer from './components/CreateGridDrawer/CreateGridDrawer';
import CreateFolderDrawer from './components/CreateGridDrawer/CreateFolderDrawer';
import ApplicationsDrawer from './components/ApplicationsDrawer/ApplicationsDrawer';
import DocumentCard from './components/DocumentCard/DocumentCard';
import DocumentList from './components/DocumentList/DocumentListItem';
import DashboardSkeleton from './components/DashboardSkeleton';
import logoText from '../../assets/DasboardIcons/logo-text.png';
import './Dashboard.css';
import { useDocument } from '../../Pages/SpreadsheetContainer/documentInfoContex';
import CreateTemplateDrawer from './components/CreateTemplateDrawer/CreateTemplateDrawer';
import TemplateGallery from './components/TemplateGallery';
import UnauthorizedView from './UnauthorizedView/UnauthorizedView';

export const TEMPLATE_CATEGORIES = [
  'Business',
  'Education',
  'Technology',
  'Marketing',
  'HR',
  'Finance',
  'Healthcare',
  'Design',
  'Legal',
  'Other',
];

const Dashboard = () => {
  const { isAuthenticated, setIsAuthenticated, setAdmins } = useDocument();
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSidebarItem, setActiveSidebarItem] = useState('all-documents');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  // Sorting: each option provides sortBy (field) and sort (direction) numeric codes required by API
  const SORT_OPTIONS = [
    { id: 1, label: 'Create Date-Desc', sortBy: 1, sort: 2 },
    { id: 2, label: 'Create Date-Asc', sortBy: 2, sort: 1 },
    { id: 3, label: 'Name-Desc', sortBy: 3, sort: 2 },
    { id: 4, label: 'Name-Asc', sortBy: 4, sort: 1 },
    { id: 5, label: 'Update Date-Desc', sortBy: 5, sort: 2 },
    { id: 6, label: 'Update Date-Asc', sortBy: 6, sort: 1 },
  ];
  const [selectedSort, setSelectedSort] = useState(SORT_OPTIONS[0]); // Default to Update Date-Desc (most recent first)
  const [sortOpen, setSortOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [myDrive, setMyDrive] = useState(false);
  const [appsDrawerOpen, setAppsDrawerOpen] = useState(false);
  const [folderDrawerOpen, setFolderDrawerOpen] = useState(false);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [parentId, setParentId] = useState('00000000-0000-0000-0000-000000000000');
  const [types, setTypes] = useState([5]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [foldersLoading, setFoldersLoading] = useState(true);

  const isFirstRender = useRef(true);


  // Handler for Shared with me
  const handleSharedWithMe = () => {
    setMyDrive(true);
    setActiveSidebarItem('my-drive');
    resetPagination();
  };

  const handleActiveItemChange = (item) => {
    setActiveSidebarItem(item);
    if (item === 'all-documents') {
      setMyDrive(false);
      setParentId('00000000-0000-0000-0000-000000000000');
      setTypes([5]);
      resetPagination();
    } else if (item === 'my-drive') {
      setMyDrive(true);
      setParentId('00000000-0000-0000-0000-000000000000');
      setTypes([5]);
      resetPagination();
    } else {
      // Assume it's a folder id
      setMyDrive(false);
      setParentId(item);
      setTypes([]);
      resetPagination();
    }
  };

  // Load documents from API
  const loadDocs = async (page = 1, size = 10, loadAll = false) => {
    setLoading(true);
    setError('');
    // Only set activeSidebarItem if not viewing a specific folder
    if (parentId === '00000000-0000-0000-0000-000000000000') {
      setActiveSidebarItem(myDrive ? 'my-drive' : 'all-documents');
    }
    try {
      // Initialize sidebar state from cookie
      try {
        const cookieMatch = document.cookie.match(/(?:^|; )sidebar_state=([^;]+)/);
        if (cookieMatch) {
          const val = cookieMatch[1] === 'true';
          setSidebarOpen(val);
        }
      } catch { }

      const payload = {
        pageNo: loadAll ? 1 : page,
        pageSize: loadAll ? 50 : size, // Load many documents for grid view
        search, // 🔥 use search param
        referenceId: '00000000-0000-0000-0000-000000000000',
        parentId,
        myDrive: myDrive,
        referenceType: 1,
        sortBy: selectedSort.sortBy,
        onlyFiles: false,
        types,
      };
      const res = await workwiseServices.getAllDocumentList(payload);
      setDocs(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (e) {
      if (e.response?.status === 401) {
        setIsAuthenticated(false);
      } else {
        setError(e?.message || 'Failed to load documents');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRename = async (payload) => {
    try {
      await workwiseServices.updateDocumentName(payload);
      message.success('Document renamed successfully');
      await loadDocs(currentPage, pageSize, viewMode === 'grid'); // Reload to reflect changes
    } catch (e) {
      console.error('Failed to rename document:', e);
      const errorMessage = e.response?.data?.message || e.message || 'Failed to rename document. Please try again.';
      message.error(errorMessage);
      throw e; // Re-throw so DocumentCard can handle it
    }
  };

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    loadDocs(page, size);
  };

  // Reset pagination when filters change
  const resetPagination = () => {
    setCurrentPage(1);
  };

  // Load folders from API
  const loadFolders = async () => {
    setFoldersLoading(true);
    try {
      const payload = {
        pageNo: 1,
        pageSize: 50,
        search: '',
        referenceId: '00000000-0000-0000-0000-000000000000',
        parentId: '00000000-0000-0000-0000-000000000000',
        myDrive: false,
        referenceType: 1,
        sortBy: 1,
        onlyFiles: false,
        types: [1],
      };
      const res = await workwiseServices.getAllDocumentList(payload);
      setFolders(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (e) {
      if (e.response?.status === 401) {
        setIsAuthenticated(false);
      } else {
        console.error('Failed to load folders:', e);
      }
    } finally {
      setFoldersLoading(false);
    }
  };

  // Load templates from API
  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await templateService.getAllTemplates();
      setTemplates(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (e) {
      console.error('Failed to load templates:', e);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Load admin list from API and save to global context
  const loadAdmins = async () => {
    setAdminsLoading(true);
    try {
      const adminRes = await adminService.getAllAdmins();
      const adminsData = Array.isArray(adminRes?.data?.data) ? adminRes.data.data : [];
      setAdmins(adminsData);
      return adminsData;
    } catch (adminErr) {
      console.error('Failed to load admin templates:', adminErr);
      setAdmins([]);
      return [];
    } finally {
      setAdminsLoading(false);
    }
  };

  // Load documents on search change (debounced)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    let isMounted = true;

    async function load() {
      resetPagination();
      await loadDocs(1, pageSize, viewMode === 'grid'); // Load all for grid, paginated for list
    }

    // debounce to avoid firing API on every keystroke instantly
    const timeout = setTimeout(load, 400);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
  }, [search]);

  // Load documents on other filter changes (immediate)
  useEffect(() => {
    resetPagination();
    loadDocs(1, pageSize, viewMode === 'grid');
  }, [selectedSort, myDrive, parentId, types, viewMode]);

  // Load folders on mount
  useEffect(() => {
    loadFolders();
  }, []);

  // Load admins on mount
  useEffect(() => {
    loadAdmins();
  }, []);

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Persist sidebar state in cookie
  useEffect(() => {
    try {
      document.cookie = `sidebar_state=${sidebarOpen}; path=/; max-age=${60 * 60 * 24 * 7
        }`;
    } catch { }
  }, [sidebarOpen]);

  // Keyboard shortcut Ctrl/Cmd + B
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        setSidebarOpen((s) => !s);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function openLoginWindow() {
    const width = 900; // Desired width in pixels
    const height = 600; // Desired height in pixels
    const url = "https://workw.com/login"; // Or any other URL you want to load
    const windowName = "Workwise Authentication";
    const left = (window.screen.width / 2) - (width / 2);
    const top = 50;
    // Define window features
    const features = `width=${width},height=${height},left=${left},top=${top},
                    toolbar=no,menubar=no,scrollbars=no,resizable=yes,
                    location=no,directories=no,status=no`;
    // const features = `width=${width},height=${height},resizable=yes,scrollbars=yes`;

    window.open(url, windowName, features);
  }

  // support/help button removed; unauthorized UI moved to UnauthorizedView component

  const isDashboardLoading = loading || foldersLoading || templatesLoading || adminsLoading;

  return (
    <div className="dashboard-container">
      <DashboardSkeleton loading={isDashboardLoading} templateCount={8} documentCount={8}>
        <div className="dashboard-flex">
          {isAuthenticated && (
            <AnimatePresence initial={false}>
              <motion.div
                key={sidebarOpen ? 'open' : 'closed'}
                initial={{ width: sidebarOpen ? '22rem' : '4rem' }}
                animate={{ width: sidebarOpen ? '22rem' : '4rem' }}
                exit={{ width: '4rem' }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="relative"
              >
                <Sidebar
                  open={sidebarOpen}
                  onToggle={() => setSidebarOpen((s) => !s)}
                  search={search}
                  onSearch={setSearch}
                  onNewDocument={() => setCreateOpen(true)}
                  onSharedWithMe={handleSharedWithMe}
                  activeItem={activeSidebarItem}
                  onActiveItemChange={handleActiveItemChange}
                  onNewFolder={() => setFolderDrawerOpen(true)}
                  folders={folders}
                />
              </motion.div>
            </AnimatePresence>
          )}
          {isAuthenticated && sidebarOpen && (
            <div
              onClick={() => setSidebarOpen(false)}
              className="sidebar-overlay"
              style={{ display: 'block' }}
            />
          )}
          <main className="dashboard-main">
            {!isAuthenticated ? (
              <UnauthorizedView onLogin={openLoginWindow} />
            ) : (
              <>
                <Header
                  viewMode={viewMode}
                  onToggleSidebar={() => {
                    console.log('Hamburger clicked, opening drawer');
                    setAppsDrawerOpen(true);
                  }}
                  onChangeView={setViewMode}
                  sortDropdownOpen={sortOpen}
                  onToggleSort={() => setSortOpen(o => !o)}
                  sortOptions={SORT_OPTIONS}
                  selectedSort={selectedSort}
                  onSelectSort={(opt) => { setSelectedSort(opt); setSortOpen(false); }}
                />

                <CreateGridDrawer
                  open={createOpen}
                  onClose={() => { setCreateOpen(false); setSelectedTemplate(null); }}
                  onCreate={() => loadDocs(currentPage, pageSize, viewMode === 'grid')}
                  template={selectedTemplate}
                />

                <CreateFolderDrawer
                  open={folderDrawerOpen}
                  onClose={() => setFolderDrawerOpen(false)}
                  onCreate={() => {
                    loadDocs(currentPage, pageSize, viewMode === 'grid');
                    loadFolders();
                  }}
                />

                <CreateTemplateDrawer
                  open={createTemplateOpen}
                  onClose={() => setCreateTemplateOpen(false)}
                  onCreate={loadTemplates}
                />

                <ApplicationsDrawer
                  open={appsDrawerOpen}
                  onClose={() => setAppsDrawerOpen(false)}
                />

                <div className="main-content">
                  {error ? (
                    <div className="error-container">
                      <div className="error-content">
                        {error}
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Templates Gallery */}
                      <TemplateGallery
                        templates={templates}
                        onCreateBlank={() => { setSelectedTemplate(null); setCreateOpen(true); }}
                        onOpenTemplate={(template) => { setSelectedTemplate(template); setCreateOpen(true); }}
                        onCreateTemplate={() => setCreateTemplateOpen(true)}
                      />
                      {/* Recent Documents */}
                      <h2 className="text-[1.12rem] font-semibold text-gray-900 mb-4">Recent documents</h2>
                      {viewMode === 'grid' ? (
                        <div className="documents-grid">
                          {docs.filter(doc => doc.documentType === 5).map((doc, i) => (
                            <motion.div
                              key={doc.id}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.03 }}
                            >
                              <motion.div
                                whileHover={{ y: -4 }}
                                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                              >
                                <DocumentCard doc={doc} onRename={handleRename} />
                              </motion.div>
                            </motion.div>
                          ))}
                          {docs.filter(doc => doc.documentType === 5).length === 0 && (
                            <div className="empty-state">
                              No documents found
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="documents-list">
                          <DocumentList
                            docs={docs.filter(doc => doc.documentType === 5)}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            onPaginationChange={handlePaginationChange}
                          />
                          {docs.filter(doc => doc.documentType === 5).length === 0 && (
                            <div className="empty-state">
                              No documents found
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </main>
        </div>
      </DashboardSkeleton>
    </div>
  );
};

export default Dashboard;